import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import useRetryFetch from "../../hooks/useRetryFetch";
import { nowToday, reflectNow } from "../../api/now";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

type Tried = "yes" | "partly" | "no";
const EVENING_HOUR = 17;

interface Props {
  now?: () => Date;
  /** Inside Today's For now card (spec 2026-11-04 §2.4): answers only — the card shows the title and step. */
  embedded?: boolean;
  onSaved?: () => void;
}

/** "Earlier today you planned to … Did you try it?" (spec 2026-11-03 Now §1.7). */
const NowEveningCard = ({ now = () => new Date(), embedded = false, onSaved }: Props) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const qc = useQueryClient();
  const evening = now().getHours() >= EVENING_HOUR;
  const { data } = useQuery({ queryKey: ["now-today"], queryFn: () => nowToday(retryFetch), enabled: evening, staleTime: 60_000 });
  const [tried, setTried] = useState<Tried | null>(null);
  const [note, setNote] = useState("");
  const [savedId, setSavedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const item = data?.items?.[0];
  // After a save the list refetches; when another item comes up, start fresh for it.
  useEffect(() => {
    if (savedId && item && item.sessionId !== savedId) {
      setSavedId(null);
      setTried(null);
      setNote("");
    }
  }, [savedId, item]);

  const saved = savedId !== null && (!item || item.sessionId === savedId);
  if (!evening || (!item && !saved)) return null;
  if (saved)
    return (
      <p className={embedded ? "text-sm text-primary" : "rounded-2xl border border-border bg-card p-4 text-sm text-primary"} role="status">
        {t("now.evening.thanks")}
      </p>
    );
  if (!item) return null;

  const save = async () => {
    if (!tried) return;
    setBusy(true);
    setFailed(false);
    try {
      await reflectNow(retryFetch, item.sessionId, { tried, ...(note.trim() ? { note: note.trim() } : {}) });
      setSavedId(item.sessionId);
      onSaved?.();
      qc.invalidateQueries({ queryKey: ["now-today"] });
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={embedded ? "space-y-3" : "rounded-2xl border border-border bg-card p-4 space-y-3"}>
      {!embedded && (
        <>
          <div>
            <p className="text-xs text-muted-foreground">{t("now.evening.title")}</p>
            <p className="text-sm font-medium text-foreground">{item.stepText}</p>
          </div>
          <p className="text-sm">{t("now.evening.ask")}</p>
        </>
      )}
      <div className="flex flex-wrap gap-1.5">
        {(["yes", "partly", "no"] as Tried[]).map((k) => (
          <button
            key={k}
            type="button"
            aria-pressed={tried === k}
            onClick={() => setTried(k)}
            className={cn("min-h-11 rounded-full border px-4 text-sm", tried === k ? "border-primary bg-primary/10" : "border-border text-muted-foreground")}
          >
            {t(`now.evening.${k}`)}
          </button>
        ))}
      </div>
      {tried && (
        <>
          <label className="sr-only" htmlFor="now-evening-note">{t("now.evening.note")}</label>
          <textarea
            id="now-evening-note"
            aria-label={t("now.evening.note")}
            placeholder={t("now.evening.note")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={2000}
            className="w-full rounded-xl border border-border bg-background p-3 text-sm"
          />
          {failed && <p role="alert" className="text-sm text-destructive">{t("now.evening.error")}</p>}
          <div className="flex justify-end">
            <Button className="rounded-full min-h-11" disabled={busy} onClick={save}>{t("now.evening.save")}</Button>
          </div>
        </>
      )}
    </section>
  );
};

export default NowEveningCard;
